import { path, pathOr } from "ramda";

import tinycolor from "tinycolor2";
import { ThemeOptions } from "@material-ui/core/styles";

import { getContrastText, hexToHSLA } from "utils/colors";

export const PRIMARY_DARK_MODE = "#4CD7D0";
export const PRIMARY = "#245A6E";
export const SECONDARY = "#4EC1EC";
export const SUCCESS = "#2ABCB6";
export const WARNING = "#FFA800";
export const ERROR = "#FF6B6B";
export const TEXT_PRIMARY = "#41404A";
export const TEXT_PRIMARY_DARK_MODE = getContrastText(
  TEXT_PRIMARY,
  TEXT_PRIMARY,
);

const lightenRate = 7.5;
const darkenRate = 15;
const threshold = (isLight: boolean) => (isLight ? 500 : 128);

const defaultTheme = (themeOptions: ThemeOptions): ThemeOptions => {
  const mode = pathOr("light", ["palette", "type"], themeOptions);
  const isLight = mode === "light";
  const primary = pathOr(
    isLight ? PRIMARY : PRIMARY_DARK_MODE,
    ["palette", "primary", "main"],
    themeOptions,
  );
  const secondary = pathOr(
    SECONDARY,
    ["palette", "secondary", "main"],
    themeOptions,
  );

  const primaryText = pathOr(
    isLight ? TEXT_PRIMARY : TEXT_PRIMARY_DARK_MODE,
    ["palette", "text", "primary"],
    themeOptions,
  );

  const palette =
    primary && secondary
      ? {
          primary: {
            light: tinycolor(primary).lighten(lightenRate).toHexString(),
            dark: tinycolor(primary).darken(darkenRate).toHexString(),
            main: primary,
            contrastText: getContrastText(TEXT_PRIMARY, primary),
          },
          secondary: {
            light: tinycolor(secondary).lighten(lightenRate).toHexString(),
            dark: tinycolor(secondary).darken(darkenRate).toHexString(),
            main: secondary,
            contrastText: tinycolor(getContrastText(TEXT_PRIMARY, primary))
              .lighten(lightenRate)
              .toHexString(),
          },
          success: {
            light: tinycolor(SUCCESS).lighten(lightenRate).toHexString(),
            dark: tinycolor(SUCCESS).darken(darkenRate).toHexString(),
            main: SUCCESS,
            contrastText: getContrastText(
              TEXT_PRIMARY,
              SUCCESS,
              threshold(isLight),
            ),
          },
          warning: {
            light: tinycolor(WARNING).lighten(lightenRate).toHexString(),
            dark: tinycolor(WARNING).darken(darkenRate).toHexString(),
            main: WARNING,
            contrastText: getContrastText(
              TEXT_PRIMARY,
              WARNING,
              threshold(isLight),
            ),
          },
          error: {
            light: tinycolor(ERROR).lighten(lightenRate).toHexString(),
            dark: tinycolor(ERROR).darken(darkenRate).toHexString(),
            main: ERROR,
            contrastText: getContrastText(
              TEXT_PRIMARY,
              ERROR,
              threshold(isLight),
            ),
          },
          ...(isLight && {
            grey: {
              50: "#f4f3f3",
              100: "#faf9fc",
              200: "#f9f8fe",
              300: "#dee2e6",
              400: "#f2f1fa",
              A100: "#cedde6",
            },
          }),
          action: {
            selected: hexToHSLA(secondary, 0.1),
            active: hexToHSLA(secondary, isLight ? 0.1 : 0.7),
            hover: hexToHSLA(secondary, 0.15),
            focus: hexToHSLA(secondary, 0.15),
            hoverOpacity: 0.07,
            disabledBackground: isLight
              ? "rgba(241, 247, 255, 0.52)"
              : "rgba(255, 255, 255, 0.12)",
          },
          text: {
            primary: isLight ? TEXT_PRIMARY : TEXT_PRIMARY_DARK_MODE,
            secondary: isLight
              ? tinycolor(TEXT_PRIMARY).lighten(lightenRate).toHexString()
              : tinycolor(TEXT_PRIMARY_DARK_MODE)
                  .darken(darkenRate)
                  .toHexString(),
            hint: isLight ? "#B9B9B9" : "rgba(255, 255, 255, 0.5)",
          },
          background: {
            default: isLight ? "#f6f7ff" : "#303030",
            paper: isLight ? "#ffffff" : "#404040",
          },
          type: mode,
        }
      : {};

  return {
    typography: {
      fontSize: 13,
      // eslint-disable-next-line quotes
      fontFamily: `"Roboto", "Helvetica", "Arial", "Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", sans-serif`,
    },
    palette,
    shadows: [
      "none",
      "0px 0px 11px 0px rgba(0,0,0,0.005), 0px 1px 8px 0px rgba(12,0,0,0.05), 0px 0px 5px 0px rgba(0,0,0,0.05)",
      "0px 0px 8px 0px rgba(0,0,0,0.015),0px 1.5px 4.5px 0px rgba(0,0,0,0.07),0px 0px 3px 0px rgba(0,0,0,0.07)",
      "0px 0px 8px 0px rgba(0,0,0,0.015),0px 2px 5px 0px rgba(0,0,0,0.08),0px 0px 10px 0px rgba(0,0,0,0.09)",
      "0px 0px 8px 0px rgba(0,0,0,0.02), 0px 0px 8px 0px rgba(0,0,0,0.08), 0px 0px 8px 0px rgba(0,0,0,0.09)",
      "0px 1px 5px -1px rgba(0,0,0,0.02),0px 6px 10px 0px rgba(0,0,0,0.08),0px 1px 18px 0px rgba(0,0,0,0.1)",
      "0px 2px 5px -1px rgba(0,0,0,0.02),0px 7px 10px 1px rgba(0,0,0,0.08),0px 2px 16px 1px rgba(0,0,0,0.1)",
      "0px 3px 5px -2px rgba(0,0,0,0.02),0px 8px 10px 1px rgba(0,0,0,0.08),0px 2.5px 14px 1.5px rgba(0,0,0,0.1)",
      "0px 4px 6px -2px rgba(0,0,0,0.02),0px 9px 12px 1px rgba(0,0,0,0.08),0px 2.5px 16px 1.5px rgba(0,0,0,0.1)",
      "0px 5px 6px -3px rgba(0,0,0,0.02),0px 10px 14px 1px rgba(0,0,0,0.08),0px 3px 18px 2px rgba(0,0,0,0.1)",
      "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
      "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
      "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
      "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
      "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
      "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
      "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
      "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
      "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
      "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
      "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)",
    ],
    overrides: {
      MuiTypography: {
        root: {
          fontSize: 13,
        },
        body1: {
          fontSize: "0.8rem",
        },
        h1: {
          fontSize: "2.5rem",
        },
        h2: {
          fontSize: "2rem",
        },
        h3: {
          fontSize: "1.75rem",
        },
        h4: {
          fontSize: "1.5rem",
        },
        h5: {
          fontSize: "1.25rem",
        },
        h6: {
          fontSize: "1.05rem",
        },
      },
      MuiButton: {
        root: {
          whiteSpace: "nowrap",
          "&:hover": {
            filter: "contrast(0.9)",
          },
          color: primaryText,
        },
        contained: {
          color: primaryText,
        },
        outlined: {
          border: `1px solid ${primaryText}`,
        },
      },
      MuiIcon: {
        root: {
          fontSize: "1.1rem",
        },
      },
      MuiSvgIcon: {
        root: {
          fontSize: "1.1rem",
        },
        fontSizeSmall: {
          fontSize: "0.9rem",
        },
        fontSizeLarge: {
          fontSize: "1.5rem",
        },
      },
      MuiInputBase: {
        root: {
          fontSize: ".85rem",
        },
      },
      MuiFormLabel: {
        root: {
          fontSize: ".85rem",
        },
      },
      MuiSelect: {
        icon: {
          color: primaryText,
        },
        select: {
          "&:focus": {
            backgroundColor: path(
              ["palette", "action", "selected"],
              themeOptions,
            ),
          },
        },
      },
      MuiTableCell: {
        head: {
          fontSize: "0.85rem",
        },
        body: {
          fontSize: "0.8rem",
        },
      },
      MuiTableSortLabel: {
        root: {
          width: "100%",
          "&:hover": {
            "& $icon": {
              color: primary,
            },
          },
          ".MuiTableCell-alignCenter > &": {
            justifyContent: "center",
          },
          "&$active": {
            "&& $icon": {
              color: primary,
            },
          },
        },
      },
      MuiIconButton: {
        root: {
          "&:hover": {
            filter: "contrast(0.9)",
            ...(isLight && {
              backgroundColor: "rgba(231, 241, 255, 0.45)",
            }),
          },
          ...(isLight && {
            color: "#6f727d",
          }),
        },
      },
      MuiFormHelperText: {
        marginDense: {
          marginTop: 0,
        },
      },
      MuiListItemIcon: {
        root: {
          color: path(["palette", "text", "secondary"], themeOptions),
          minWidth: "30px",
        },
      },
      MuiCardContent: {
        root: {
          "&:last-child": {
            paddingBottom: "",
          },
        },
      },
    },
    props: {
      MuiTextField: {
        variant: "outlined",
        margin: "dense",
        InputLabelProps: { shrink: true },
      },
      MuiButton: {
        variant: "outlined",
      },
    },
  };
};

export default defaultTheme;
